/**
 * KAN-950: tutorial modal. Plays the given video and lists every video of
 * its section (registry `section`); a click on the list switches the video.
 * One video in the section = no list. Shared by the page tutorial button and
 * the onboarding button (KAN-952).
 *
 * The parent remounts it on every opening (key), so the picked video resets
 * to the starting one.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ListVideo } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { sectionVideos, type SectionVideo } from '@/tutorials/sections';
import { TutorialVideo } from './TutorialVideo';
import { TutorialSectionList } from './TutorialSectionList';

interface TutorialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Video the modal starts with; used as is when the registry does not list it. */
  start: SectionVideo;
}

export function TutorialDialog({ open, onOpenChange, start }: TutorialDialogProps) {
  const [pickedSlug, setPickedSlug] = useState(start.slug);
  const videos = useMemo(() => sectionVideos(start.slug), [start.slug]);
  const playing = videos.find((video) => video.slug === pickedSlug) ?? start;
  const showList = videos.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={showList ? 'max-w-6xl' : 'max-w-4xl'}>
        <DialogHeader>
          <DialogTitle>{playing.title}</DialogTitle>
          <DialogDescription className="sr-only">Видео-подсказка по этой странице</DialogDescription>
        </DialogHeader>
        <div className={cn(showList && 'grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]')}>
          {open && <TutorialVideo key={playing.videoUrl} src={playing.videoUrl} />}
          {showList && (
            <div className="max-h-[40vh] overflow-y-auto lg:max-h-[60vh]">
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Ролики раздела
              </p>
              <TutorialSectionList
                videos={videos}
                currentSlug={playing.slug}
                onSelect={setPickedSlug}
              />
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Link
            to="/help"
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            <ListVideo className="h-4 w-4" aria-hidden="true" />
            Все ролики
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
